import * as React from 'react';
import { styled } from '@linaria/react';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import Demo from '@/components/Demo';
import ButtonPrimary from '@/components/ButtonPlayful/ButtonPrimary';

import {
  Svg,
  CodeSnippet,
  Row,
} from '../BasicShapesDemo/BasicShapes.shared';
import { Pause, Play } from 'lucide-react';

const WIDTH = 250;
const HEIGHT = 340;

function FancyLoadingSpinner() {
  const [animationState, setAnimationState] = React.useState<
    'running' | 'paused'
  >('paused');
  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    if (prefersReducedMotion) {
      setAnimationState('paused');
    }
  }, [prefersReducedMotion]);

  return (
    <Demo
      outputWrapper={OutputWrapper}
      initialValues={{}}
      customGrid={{
        gridTemplateColumns: 'min(400px, 100%)',
        justifyContent: 'center',
      }}
    >
      {({}) => {
        const code = `\
@keyframes grow {
  from {
    stroke-dasharray:
      calc(var(--circumference) * 0.05),
      var(--circumference);
  }
  to {
    stroke-dasharray:
      calc(var(--circumference) * 0.25),
      var(--circumference);
  }
}
\n \n
@keyframes spin {
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset:
      calc(var(--circumference) * -0.95);
  }
}
\n \n
circle {
  /*
    Calculate the circumference in JS, and
    apply it as a custom property:
  */
  --circumference: 572px;
  \n
  animation:
    grow 1200ms infinite alternate${animationState === 'running' ? '' : ' paused'},
    spin 2400ms infinite alternate${animationState === 'running' ? '' : ' paused'};
}
`;
        const circleRadius = WIDTH * 0.325;

        const circleCircumference = circleRadius * 2 * Math.PI;

        return (
          <>
            <StyledRow>
              <SvgWrapper>
                <Svg
                  width={WIDTH}
                  height={HEIGHT}
                  viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                >
                  <Group>
                    <Circle
                      cx={WIDTH / 2}
                      cy={HEIGHT / 2}
                      r={circleRadius}
                      fill="none"
                      stroke="var(--wham-complementary-500)"
                      style={{
                        animationPlayState: animationState,
                        '--circumference': circleCircumference + 'px',
                      }}
                    />
                  </Group>
                </Svg>
              </SvgWrapper>

              <StyledCodeSnippet lang="css" code={code} />
            </StyledRow>
            <StyledButton
              textAlign="true-center"
              Icon={animationState === 'running' ? Pause : Play}
              onClick={() =>
                setAnimationState(
                  animationState === 'running' ? 'paused' : 'running'
                )
              }
            >
              {animationState === 'running' ? 'Pause' : 'Play'}
            </StyledButton>
          </>
        );
      }}
    </Demo>
  );
}

const BREAKPOINT = 42;

const OutputWrapper = styled.div`
  container-type: inline-size;
  background: var(--color-background);
`;

const StyledRow = styled(Row)`
  @container (max-width: ${BREAKPOINT}rem) {
    flex-direction: column-reverse;
    align-items: center;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  --inline-block-override: 22px;
  --inline-padding-override: 24px;
  max-height: ${HEIGHT}px;
  overflow: auto;

  @container (max-width: ${BREAKPOINT}rem) {
    width: 100%;
    max-height: 530px;
  }
`;

const SvgWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  /* @container (max-width: ${BREAKPOINT}rem) {
    position: relative;
  } */
`;

const Group = styled.g`
  transform: rotate(-90deg);
  transform-origin: center center;
`;

const Circle = styled.circle`
  @keyframes tweakDashArray {
    from {
      stroke-dasharray: calc(var(--circumference) * 0.05),
        var(--circumference);
    }
    to {
      stroke-dasharray: calc(var(--circumference) * 0.25),
        var(--circumference);
    }
  }

  @keyframes tweakOffset {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: calc(var(--circumference) * -0.95);
    }
  }

  --curve: ease-in-out;

  stroke-width: 10px;
  stroke-linecap: round;
  animation:
    tweakDashArray 1200ms infinite alternate var(--curve),
    tweakOffset 2400ms infinite alternate var(--curve);
`;

const StyledButton = styled(ButtonPrimary)`
  margin-inline: auto;
  width: 16.25rem;
  max-width: 100%;
`;

export default FancyLoadingSpinner;
